import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFirebase } from '../contexts/FirebaseContext';
import { routeForRole } from '../utils/roleRouter';
import { LogIn, Mail, Lock, AlertCircle } from 'lucide-react';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const { db } = useFirebase();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const user = await login(email, password);
      // One platform: route to the portal matching users/{uid}.role (iOS parity)
      const destination = await routeForRole(db, user?.uid);
      navigate(destination, { replace: true });
    } catch (err) {
      setError('Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center px-4 py-8 overflow-y-auto">
      <div className="max-w-md md:max-w-2xl w-full space-y-8">
        <div className="text-center fade-in">
          <img
            src="/ChefLogo.jpg"
            alt="CheF"
            className="w-[120px] h-[120px] rounded-full object-cover mx-auto mb-4 shadow-md ring-[3px] ring-[#FF9500]"
          />
          <h2 className="text-4xl font-bold gradient-text mb-2 text-center">Welcome Back</h2>
          <p className="text-gray-600 text-lg">
            Sign in to your CheF account 👨‍🍳
          </p>
        </div>

        <div className="card p-8 slide-up">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-base font-bold text-gray-900 mb-2">
                  📧 Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail size={20} className="text-orange-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="modern-input w-full pl-12 pr-4 py-4 text-lg"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-base font-bold text-gray-900 mb-2">
                  🔒 Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock size={20} className="text-orange-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="modern-input w-full pl-12 pr-4 py-4 text-lg"
                    placeholder="Enter your password"
                  />
                </div>
              </div>
            </div>

            {error && (
              <div className="error-card p-4">
                <div className="error-icon">
                  <AlertCircle size={24} />
                </div>
                <p className="font-medium">{error}</p>
              </div>
            )}

            <div className="pt-12">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full btn-primary py-4 text-lg font-bold shadow-2xl hover:shadow-orange-200/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="loading-spinner mr-3"></div>
                    <span>🔐 Signing in...</span>
                  </div>
                ) : (
                  <span>🚀 Sign In</span>
                )}
              </button>
            </div>

            <div className="text-center pt-4">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-red-600 hover:text-red-700 font-medium text-sm mb-4"
              >
                Forgot Password?
              </button>
              <p className="text-gray-600 mb-4">
                Don't have an account?
              </p>
              <Link to="/signup" className="btn-primary py-3 px-6 inline-block font-bold">
                Create Account
              </Link>
            </div>
          </form>
        </div>

        {/* Food decoration */}
        <div className="text-center text-4xl opacity-40 pb-8">
          🍔🍟🥤🍗🍕
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
